import {Card} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  CommunicationActivityLogDatagrid,
  DmsDataGrid,
  openActivityChangeLog,
  Section,
  SectionTab,
} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['commandId'] as const;

export function ActionLog() {
  const {id} = useRequiredParams();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {record: id}]),
    [id]
  );

  const emailActivityLogQueryModifier = useCallback(
    (filter: QueryFilterObject) => ({...filter, recordId: id, customerId: undefined}),
    [id]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const {actionKey, getColumnData} = data;
    const commandId = getColumnData('commandId')?.value;

    match(actionKey)
      .with('detail', () => {
        if (commandId && typeof commandId === 'string') {
          openActivityChangeLog({
            resourceId: EntityResourceIds.workshopInterest,
            commandId,
            recordId: id,
          });
        }
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const tabs: SectionTab[] = [
    {
      content: (
        <Card title={i18n.t('general.labels.activityLogs')} key="activity-logs-datagrid">
          <Box>
            <DmsDataGrid
              actionCallback={actionCallback}
              predefinedColumns={PREDEFINED_COLUMNS}
              gridCode="workshop-interest-audit-log"
              autoHeight
              queryModifier={queryModifier}
              data-testid="workshop-interest-activity-logs-datagrid"
            />
          </Box>
        </Card>
      ),
      title: i18n.t('general.labels.activityLogs'),
      queryId: 'workshop-interest-action-log',
    },
    {
      content: (
        <Card title={i18n.t('general.labels.emailActivityLogs')} key="email-activity-logs-datagrid">
          <Box height={130}>
            <CommunicationActivityLogDatagrid
              queryModifier={emailActivityLogQueryModifier}
              presetInstanceId="workshop-interest-email-activity-logs"
              data-testid="workshop-interest-email-activity-logs-datagrid"
            />
          </Box>
        </Card>
      ),
      title: i18n.t('general.labels.emailActivityLogs'),
      queryId: 'workshop-interest-email-activity-log',
    },
  ];

  return <Section tabs={tabs} />;
}
